"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useWallet } from "../contexts/WalletContext"
import { History, Filter, Download, Search } from "lucide-react"
import Card from "../components/Card"
import Input from "../components/Input"
import Button from "../components/Button"

const Transactions = () => {
  const { user } = useAuth()
  const { transactions } = useWallet()
  const navigate = useNavigate()

  const [filteredTransactions, setFilteredTransactions] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [filterType, setFilterType] = useState("all")
  const [filterStatus, setFilterStatus] = useState("all")
  const [dateRange, setDateRange] = useState("all")

  useEffect(() => {
    if (!user) {
      navigate("/login")
    }
  }, [user, navigate])

  useEffect(() => {
    filterTransactions()
  }, [transactions, searchTerm, filterType, filterStatus, dateRange])

  const filterTransactions = () => {
    let filtered = [...transactions]

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (transaction) =>
          transaction.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transaction.network?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transaction.phone?.includes(searchTerm) ||
          transaction.transactionId?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    // Type filter
    if (filterType !== "all") {
      filtered = filtered.filter((transaction) => transaction.type.toLowerCase().includes(filterType.toLowerCase()))
    }

    // Status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((transaction) => transaction.status === filterStatus)
    }

    // Date range filter
    if (dateRange !== "all") {
      const now = new Date()
      const filterDate = new Date()

      switch (dateRange) {
        case "today":
          filterDate.setHours(0, 0, 0, 0)
          break
        case "week":
          filterDate.setDate(now.getDate() - 7)
          break
        case "month":
          filterDate.setMonth(now.getMonth() - 1)
          break
        default:
          break
      }

      if (dateRange !== "all") {
        filtered = filtered.filter((transaction) => new Date(transaction.date) >= filterDate)
      }
    }

    setFilteredTransactions(filtered)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-100"
      case "pending":
        return "text-yellow-600 bg-yellow-100"
      case "failed":
        return "text-red-600 bg-red-100"
      default:
        return "text-gray-600 bg-gray-100"
    }
  }

  const getTypeIcon = (type) => {
    if (type.toLowerCase().includes("data")) return "📱"
    if (type.toLowerCase().includes("airtime")) return "📞"
    if (type.toLowerCase().includes("cable")) return "📺"
    if (type.toLowerCase().includes("electricity")) return "⚡"
    return "💳"
  }

  const exportTransactions = () => {
    const csvContent = [
      ["Date", "Type", "Network/Provider", "Phone", "Amount", "Status", "Transaction ID"],
      ...filteredTransactions.map((t) => [
        new Date(t.date).toLocaleDateString(),
        t.type,
        t.network || t.provider || "",
        t.phone || "",
        t.amount,
        t.status,
        t.transactionId || "",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `transactions_${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  if (!user) return null

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <History className="mr-3 text-blue-600" />
            Transaction History
          </h1>
          <p className="text-gray-600 mt-2">View and manage all your transactions</p>
        </div>

        {/* Filters */}
        <Card className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Input
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="data">Data Purchase</option>
              <option value="airtime">Airtime Purchase</option>
              <option value="cable">Cable TV</option>
              <option value="electricity">Electricity</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>

            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>

            <Button
              onClick={exportTransactions}
              variant="outline"
              className="flex items-center space-x-2"
              disabled={filteredTransactions.length === 0}
            >
              <Download size={16} />
              <span>Export</span>
            </Button>
          </div>
        </Card>

        {/* Transaction Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{transactions.length}</p>
              <p className="text-sm text-gray-600">Total Transactions</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {transactions.filter((t) => t.status === "completed").length}
              </p>
              <p className="text-sm text-gray-600">Completed</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">
                {transactions.filter((t) => t.status === "pending").length}
              </p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">
                ₦{transactions.reduce((sum, t) => sum + (t.amount || 0), 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-600">Total Spent</p>
            </div>
          </Card>
        </div>

        {/* Transactions List */}
        <Card>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Transactions ({filteredTransactions.length})</h2>
            <Filter className="h-5 w-5 text-gray-400" />
          </div>

          {filteredTransactions.length > 0 ? (
            <div className="space-y-4">
              {filteredTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="text-2xl">{getTypeIcon(transaction.type)}</div>
                    <div>
                      <p className="font-semibold">{transaction.type}</p>
                      <p className="text-sm text-gray-600">
                        {transaction.network || transaction.provider}
                        {transaction.phone && ` • ${transaction.phone}`}
                      </p>
                      <p className="text-xs text-gray-500">{new Date(transaction.date).toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="font-bold text-lg">-₦{transaction.amount?.toLocaleString()}</p>
                    <span
                      className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}
                    >
                      {transaction.status}
                    </span>
                    {transaction.transactionId && (
                      <p className="text-xs text-gray-500 mt-1">ID: {transaction.transactionId}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <History className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h3>
              <p className="text-gray-600 mb-6">
                {transactions.length === 0
                  ? "You haven't made any transactions yet."
                  : "No transactions match your current filters."}
              </p>
              {transactions.length === 0 && (
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button onClick={() => navigate("/buy-data")}>Buy Data</Button>
                  <Button variant="outline" onClick={() => navigate("/buy-airtime")}>
                    Buy Airtime
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

export default Transactions
